import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Progress,
  SimpleGrid,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, { useMemo, useState, useCallback } from 'react';
import {
  FiAward,
  FiBarChart,
  FiBookOpen,
  FiDownload,
  FiRefreshCw,
  FiTarget,
  FiTrendingUp,
} from 'react-icons/fi';

import {
  AdminLayout,
  Button,
  Card,
  FormField,
  SelectInput,
  StatsCard,
} from '@grcs/components';

import {
  ArticleEvaluationResult,
  evaluateArticle,
} from '@grcs/utils/content/evaluateArticle';
import { countArticleCharacters } from '@grcs/utils/content/countArticleCharacters';
import { BarChart } from '@grcs/views/shared/components/charts/BarChart';
import PieChart from '@grcs/views/shared/components/charts/PieChart';
import { ArticleEditor } from '@grcs/views/shared/components/editors/ArticleEditor';
import { useData } from '@grcs/context/DataContext';
import { TABLE_NAMES } from '@grcs/utils/database/types';
import { ArticleProfile } from '@grcs/types/DataTypes';

interface EvaluationResult {
  wordCount: number;
  uniqueWords: number;
  gradeDistribution: Record<string, number>;
  cumulativeDistribution: Record<string, number>;
  difficultyLevel: 'Easy' | 'Moderate' | 'Challenge' | 'Difficult';
  readabilityScore: number;
  recommendations: string[];
  vocabularyAnalysis: {
    grade: string;
    words: string[];
    percentage: number;
  }[];
}

interface ToolsProps {
  active?: boolean;
  embedded?: boolean;
}

type WordCountOrder = 'asc' | 'desc';

type WordCountArticle = {
  id: string;
  title: string;
  word_count: number | null;
};

export const EvaluateEnhanced: React.FC<ToolsProps> = ({
  active = true,
  embedded = false,
}) => {
  const Layout = embedded ? Box : AdminLayout;
  const { supabase } = useData();
  const supportedGlossaries = [
    { label: 'GRCS', value: 'GRCS' },
    { label: 'HSK', value: 'HSK' },
  ];

  const [article, setArticle] = useState<string>('');
  const [selectedArticleMeta, setSelectedArticleMeta] = useState<{
    title: string;
    grade: string;
    chapter: string;
  } | null>(null);
  const [glossaryName, setGlossaryName] = useState<string>('');
  const [result, setResult] = useState<ArticleEvaluationResult | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [libraryArticles, setLibraryArticles] = useState<ArticleProfile[]>([]);
  const [isLibraryLoading, setIsLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState<string>('');
  const [wordCountArticles, setWordCountArticles] = useState<
    WordCountArticle[]
  >([]);
  const [isWordCountUpdating, setIsWordCountUpdating] = useState(false);
  const [wordCountError, setWordCountError] = useState('');
  const [wordCountOrder, setWordCountOrder] = useState<WordCountOrder>('asc');
  const [wordCountStatus, setWordCountStatus] = useState('');
  const [wordCountPage, setWordCountPage] = useState(1);

  const { onOpen: onResultOpen } = useDisclosure();
  const {
    isOpen: isDetailOpen,
    onOpen: onDetailOpen,
    onClose: onDetailClose,
  } = useDisclosure();
  const {
    isOpen: isLibraryOpen,
    onOpen: onLibraryOpen,
    onClose: onLibraryClose,
  } = useDisclosure();
  const {
    isOpen: isWordCountOpen,
    onOpen: onWordCountOpen,
    onClose: onWordCountClose,
  } = useDisclosure();

  // Theme colors
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Calculate enhanced evaluation results
  const evaluationResults: EvaluationResult = useMemo(() => {
    if (!result || result.totalCharacters === 0) {
      return {
        wordCount: 0,
        uniqueWords: 0,
        gradeDistribution: {},
        cumulativeDistribution: {},
        difficultyLevel: 'Easy',
        readabilityScore: 0,
        recommendations: [],
        vocabularyAnalysis: [],
      };
    }

    const totalWords = result.totalCharacters;
    const uniqueWords = result.uniqueCharacters;
    const parseLevel = (level: string) => {
      const [gradePart, chapterPart] = level.split('.');
      return {
        grade: Number.parseInt(gradePart, 10),
        chapter: chapterPart ? Number.parseInt(chapterPart, 10) : 0,
      };
    };
    const compareLevels = (a: string, b: string) => {
      const levelA = parseLevel(a);
      const levelB = parseLevel(b);
      if (levelA.grade !== levelB.grade) {
        return levelA.grade - levelB.grade;
      }
      return levelA.chapter - levelB.chapter;
    };
    const targetLevel =
      selectedArticleMeta?.grade && selectedArticleMeta?.chapter
        ? `${selectedArticleMeta.grade}.${selectedArticleMeta.chapter}`
        : '';
    const isHSK = glossaryName === 'HSK';
    const hasTargetLevel = !isHSK && targetLevel.length > 0;

    const gradeEntries = Object.entries(result.gradeCounts).sort(
      ([gradeA], [gradeB]) => compareLevels(gradeA, gradeB),
    );
    const gradePercentages = gradeEntries.map(([grade, count]) => ({
      grade,
      count,
      percentage: totalWords > 0 ? (count / totalWords) * 100 : 0,
    }));
    const significantGrades = isHSK
      ? gradePercentages
      : gradePercentages.filter(
          (entry) =>
            entry.percentage > 5 ||
            (hasTargetLevel && entry.grade === targetLevel),
        );
    const remainingGrades = gradePercentages.filter(
      (entry) =>
        !significantGrades.some(
          (significant) => significant.grade === entry.grade,
        ),
    );
    let learnedCount = 0;
    let newCount = 0;
    let otherCount = 0;
    if (hasTargetLevel) {
      remainingGrades.forEach((entry) => {
        if (compareLevels(entry.grade, targetLevel) < 0) {
          learnedCount += entry.count;
        } else {
          newCount += entry.count;
        }
      });
    } else if (!isHSK) {
      otherCount = remainingGrades.reduce((sum, entry) => sum + entry.count, 0);
    }
    const learnedPercentage =
      totalWords > 0 ? (learnedCount / totalWords) * 100 : 0;
    const newPercentage = totalWords > 0 ? (newCount / totalWords) * 100 : 0;
    const otherPercentage =
      totalWords > 0 ? (otherCount / totalWords) * 100 : 0;

    // Calculate cumulative distribution
    const cumulativeDistribution: Record<string, number> = {};
    significantGrades
      .sort((a, b) => compareLevels(a.grade, b.grade))
      .forEach(({ grade }) => {
        const cumulativeCount = result.cumulativeCounts[grade] || 0;
        cumulativeDistribution[grade] =
          totalWords > 0 ? (cumulativeCount / totalWords) * 100 : 0;
      });

    // Determine difficulty level based on grade distribution
    const highGradePercentage = isHSK
      ? (gradeEntries
          .filter(([grade]) => Number.parseInt(grade, 10) >= 4)
          .reduce((sum, [, count]) => sum + count, 0) /
          (totalWords || 1)) *
        100
      : newPercentage + otherPercentage;

    let difficultyLevel: EvaluationResult['difficultyLevel'] = 'Easy';
    if (highGradePercentage > 40) difficultyLevel = 'Difficult';
    else if (highGradePercentage > 25) difficultyLevel = 'Challenge';
    else if (highGradePercentage > 15) difficultyLevel = 'Moderate';

    // Calculate readability score (0-100, higher is easier)
    const readabilityScore = Math.max(0, 100 - highGradePercentage * 1.5);

    // Generate recommendations
    const recommendations: string[] = [];
    if (isHSK) {
      const hskTarget = gradeEntries.find(([grade]) => {
        const cumulativeCount = result.cumulativeCounts[grade] || 0;
        const coverage = totalWords > 0 ? cumulativeCount / totalWords : 0;
        return coverage >= 0.75;
      });
      if (hskTarget) {
        recommendations.push(
          `Best suited for HSK ${hskTarget[0]} based on cumulative coverage`,
        );
      }
    } else if (result.recommendedGrade) {
      recommendations.push(
        `Best suited for Grade ${result.recommendedGrade} based on learned vocabulary`,
      );
    }
    if (highGradePercentage > 30) {
      recommendations.push(
        'Consider simplifying complex vocabulary for better comprehension',
      );
    }
    if (totalWords > 500) {
      recommendations.push(
        'Article length is suitable for extended reading practice',
      );
    } else if (totalWords < 100) {
      recommendations.push(
        'Consider adding more content for comprehensive practice',
      );
    }
    if (totalWords > 0 && uniqueWords / totalWords > 0.8) {
      recommendations.push(
        'High vocabulary diversity - excellent for learning new words',
      );
    }
    if (result.missingCount > 0) {
      recommendations.push(
        `${result.missingCount} characters are not in the selected vocabulary list`,
      );
    }

    // Vocabulary analysis by grade
    const sortedSignificant = [...significantGrades].sort((a, b) =>
      compareLevels(a.grade, b.grade),
    );
    const learnedRow =
      hasTargetLevel && learnedCount > 0
        ? { grade: 'Learned', words: [], percentage: learnedPercentage }
        : null;
    const newRow =
      hasTargetLevel && newCount > 0
        ? { grade: 'New', words: [], percentage: newPercentage }
        : null;
    const vocabularyAnalysis: EvaluationResult['vocabularyAnalysis'] = [];
    if (isHSK) {
      sortedSignificant.forEach((entry) => {
        vocabularyAnalysis.push({
          grade: `Grade ${entry.grade}`,
          words: [],
          percentage: entry.percentage,
        });
      });
    } else if (hasTargetLevel && learnedRow) {
      let insertedLearned = false;
      sortedSignificant.forEach((entry) => {
        if (!insertedLearned && compareLevels(entry.grade, targetLevel) >= 0) {
          vocabularyAnalysis.push(learnedRow);
          insertedLearned = true;
        }
        vocabularyAnalysis.push({
          grade: `Grade ${entry.grade}`,
          words: [],
          percentage: entry.percentage,
        });
      });
      if (!insertedLearned) {
        vocabularyAnalysis.push(learnedRow);
      }
    } else {
      sortedSignificant.forEach((entry) => {
        vocabularyAnalysis.push({
          grade: `Grade ${entry.grade}`,
          words: [],
          percentage: entry.percentage,
        });
      });
    }
    if (!isHSK && hasTargetLevel && newRow) {
      vocabularyAnalysis.push(newRow);
    }
    if (!isHSK && !hasTargetLevel && otherCount > 0) {
      vocabularyAnalysis.push({
        grade: 'Other',
        words: [],
        percentage: otherPercentage,
      });
    }

    return {
      wordCount: totalWords,
      uniqueWords,
      gradeDistribution: {
        ...Object.fromEntries(
          sortedSignificant.map(({ grade, count }) => [grade, count]),
        ),
        ...(!isHSK && hasTargetLevel && learnedCount > 0
          ? { Learned: learnedCount }
          : {}),
        ...(!isHSK && hasTargetLevel && newCount > 0 ? { New: newCount } : {}),
        ...(!isHSK && !hasTargetLevel && otherCount > 0
          ? { Other: otherCount }
          : {}),
      },
      cumulativeDistribution,
      difficultyLevel,
      readabilityScore,
      recommendations,
      vocabularyAnalysis,
    };
  }, [result, selectedArticleMeta, glossaryName]);

  const sortedWordCountArticles = useMemo(() => {
    const sorted = [...wordCountArticles].sort((a, b) => {
      const countA = a.word_count ?? 0;
      const countB = b.word_count ?? 0;
      return wordCountOrder === 'asc' ? countA - countB : countB - countA;
    });
    return sorted;
  }, [wordCountArticles, wordCountOrder]);

  const wordCountPageSize = 15;
  const wordCountTotalPages = Math.max(
    1,
    Math.ceil(sortedWordCountArticles.length / wordCountPageSize),
  );
  const wordCountPageStart = (wordCountPage - 1) * wordCountPageSize;
  const wordCountPageItems = sortedWordCountArticles.slice(
    wordCountPageStart,
    wordCountPageStart + wordCountPageSize,
  );

  const handleArticleChanges = (value: string) => {
    const sanitizedValue = value.replace(/<img[^>]*>/gi, '');
    setArticle(sanitizedValue);
    setResult(null);
  };

  const handleUpdateArticleWordCount = useCallback(async () => {
    onWordCountOpen();
    setIsWordCountUpdating(true);
    setWordCountError('');
    setWordCountStatus('Loading article list...');

    try {
      const { data, error } = await supabase
        .from(TABLE_NAMES.LIBRARY)
        .select('id,title,word_count')
        .eq('deleted', false);

      if (error) {
        throw error;
      }

      const articles = (data as WordCountArticle[]) || [];
      setWordCountArticles(articles);
      setWordCountPage(1);
      const missingCounts = articles.filter(
        (item) =>
          item.word_count === null ||
          item.word_count === undefined ||
          item.word_count === 0,
      );

      const totalMissing = missingCounts.length;
      for (let index = 0; index < missingCounts.length; index += 1) {
        const item = missingCounts[index];
        const progressLabel = `(${index + 1}/${totalMissing})`;
        setWordCountStatus(`Loading ${progressLabel} "${item.title}"...`);
        const { data: articleData, error: articleError } = await supabase
          .from(TABLE_NAMES.LIBRARY)
          .select('id,article')
          .eq('id', item.id)
          .limit(1);

        if (articleError) {
          throw articleError;
        }

        const selected = (articleData as ArticleProfile[])?.[0];
        if (!selected?.article) {
          continue;
        }

        setWordCountStatus(`Counting ${progressLabel} "${item.title}"...`);
        const word_count = countArticleCharacters(selected.article);

        const { error: updateError } = await supabase
          .from(TABLE_NAMES.LIBRARY)
          .update({ word_count })
          .eq('id', item.id);

        if (updateError) {
          throw updateError;
        }

        setWordCountStatus(`Updating ${progressLabel} "${item.title}"...`);
        // setWordCountArticles((prev) =>
        //   prev.map((entry) =>
        //     entry.id === item.id ? { ...entry, word_count } : entry,
        //   ),
        // );
      }

      const { data: listData, error: listError } = await supabase
        .from(TABLE_NAMES.LIBRARY)
        .select('id,title,word_count')
        .eq('deleted', false);

      if (listError) {
        throw listError;
      }

      setWordCountArticles((listData as WordCountArticle[]) || []);
      setWordCountStatus('Done.');
    } catch (error: any) {
      console.error('Failed to update word counts:', error);
      setWordCountError('Failed to update word counts. Please try again.');
    } finally {
      setIsWordCountUpdating(false);
    }
  }, [supabase, onWordCountOpen]);

  const handleEvaluateClick = async () => {
    setIsEvaluating(true);

    // Simulate processing time for better UX
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      const targetLevel =
        selectedArticleMeta?.grade && selectedArticleMeta?.chapter
          ? `${selectedArticleMeta.grade}.${selectedArticleMeta.chapter}`
          : undefined;
      const evaluationResult = evaluateArticle(
        article,
        glossaryName,
        targetLevel,
      );
      setResult(evaluationResult);
      onResultOpen();
    } catch (error) {
      console.error('Evaluation error:', error);
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleOpenLibrary = useCallback(async () => {
    onLibraryOpen();
    if (libraryArticles.length > 0 || isLibraryLoading) {
      return;
    }

    setIsLibraryLoading(true);
    setLibraryError('');
    try {
      const { data, error } = await supabase
        .from(TABLE_NAMES.LIBRARY)
        .select('id,title,grade,chapter')
        .eq('deleted', false)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }
      setLibraryArticles((data as ArticleProfile[]) || []);
    } catch (error: any) {
      console.error('Failed to load library articles:', error);
      setLibraryError('Failed to load articles. Please try again.');
    } finally {
      setIsLibraryLoading(false);
    }
  }, [supabase, libraryArticles.length, isLibraryLoading, onLibraryOpen]);

  const handleSelectExistingArticle = useCallback(
    async (articleId: string) => {
      setIsLibraryLoading(true);
      setLibraryError('');
      try {
        const { data, error } = await supabase
          .from(TABLE_NAMES.LIBRARY)
          .select('id,title,grade,chapter,article')
          .eq('id', articleId)
          .limit(1);

        if (error) {
          throw error;
        }
        const selected = (data as ArticleProfile[])?.[0];
        if (!selected) {
          setLibraryError('Selected article not found.');
          return;
        }
        setArticle(selected.article || '');
        setSelectedArticleMeta({
          title: selected.title,
          grade: selected.grade,
          chapter: selected.chapter,
        });
        setResult(null);
        onLibraryClose();
      } catch (error: any) {
        console.error('Failed to load article:', error);
        setLibraryError('Failed to load article content. Please try again.');
      } finally {
        setIsLibraryLoading(false);
      }
    },
    [supabase, onLibraryClose],
  );

  const handleExportResults = () => {
    const csvContent = [
      'Grade,Word Count,Percentage,Cumulative Percentage',
      ...Object.entries(evaluationResults.gradeDistribution).map(
        ([grade, count]) => {
          const label =
            grade === 'Learned' || grade === 'New' || grade === 'Other'
              ? grade
              : `Grade ${grade}`;
          const cumulative =
            evaluationResults.cumulativeDistribution[grade] ?? 0;
          return `${label},${count},${(
            (count / evaluationResults.wordCount) *
            100
          ).toFixed(1)}%,${cumulative.toFixed(1)}%`;
        },
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vocabulary_evaluation.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy':
        return 'green';
      case 'Moderate':
        return 'blue';
      case 'Challenge':
        return 'orange';
      case 'Difficult':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <Layout padding={embedded ? 0 : 3}>
      <VStack spacing={4} align="stretch">
        {/* Header */}
        <div>
          <Text fontSize="md" fontWeight="600" mb={2}>
            Article Evaluation
          </Text>
        </div>

        {/* Tools Section */}
        <Card padding={4}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between" align="center" wrap="wrap">
              <VStack align="start" spacing={1}>
                <Text fontSize="lg" fontWeight="600">
                  Tools
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Manage library metadata and keep word counts current.
                </Text>
              </VStack>
              <Button
                leftIcon={<FiRefreshCw />}
                size="sm"
                onClick={handleUpdateArticleWordCount}
                isLoading={isWordCountUpdating}
                loadingText="Updating..."
              >
                Update Article Word Count
              </Button>
            </HStack>
          </VStack>
        </Card>

        {/* Input Section */}
        <Card padding={4}>
          <VStack spacing={6} align="stretch">
            <HStack justify="space-between" align="center" wrap="wrap">
              <VStack align="start" spacing={1}>
                <Text fontSize="lg" fontWeight="600">
                  Article
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Paste or select an existing article to evaluate.
                </Text>
              </VStack>
              <HStack spacing={3}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleOpenLibrary}
                >
                  Select Existing Article
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setArticle('');
                    setSelectedArticleMeta(null);
                    setResult(null);
                  }}
                >
                  Clear
                </Button>
              </HStack>
            </HStack>
            {selectedArticleMeta && (
              <HStack spacing={3} wrap="wrap">
                <Badge colorScheme="blue">{selectedArticleMeta.title}</Badge>
                <Badge colorScheme="green">
                  Grade {selectedArticleMeta.grade}
                </Badge>
                <Badge colorScheme="purple">
                  Chapter {selectedArticleMeta.chapter}
                </Badge>
              </HStack>
            )}
            <FormField>
              <ArticleEditor
                value={article}
                onChange={handleArticleChanges}
                style={{
                  height: '300px',
                  border: `1px solid ${borderColor}`,
                  borderRadius: '8px',
                }}
              />
            </FormField>

            <HStack spacing={6} align="end" wrap="wrap">
              <Box flex={1}>
                <FormField label="Vocabulary List" required>
                  <SelectInput
                    value={glossaryName}
                    onChange={setGlossaryName}
                    options={[...supportedGlossaries]}
                    placeholder="Choose vocabulary reference"
                  />
                </FormField>
              </Box>

              <VStack spacing={2}>
                <Button
                  leftIcon={<FiTarget />}
                  onClick={handleEvaluateClick}
                  isLoading={isEvaluating}
                  loadingText="Analyzing..."
                  isDisabled={!article.trim() || !glossaryName}
                  size="lg"
                >
                  Evaluate
                </Button>
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  {article.length} characters
                </Text>
              </VStack>
            </HStack>

            {/* Quick Info */}
            <Alert status="info" variant="subtle">
              <AlertIcon />
              <VStack align="start" spacing={1} fontSize="sm">
                <Text fontWeight="semibold">How it works:</Text>
                <Text>
                  • Analyzes each character against the selected vocabulary list
                </Text>
                <Text>• Categorizes words by grade level difficulty</Text>
                {/* <Text>
                  • Provides readability assessment and recommendations
                </Text> */}
              </VStack>
            </Alert>
          </VStack>
        </Card>

        {/* Results Section */}
        {result && result.totalCharacters > 0 && (
          <>
            {/* Summary Statistics */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
              <StatsCard
                title="Total Words"
                value={evaluationResults.wordCount.toString()}
                icon={FiBookOpen}
              />
              <StatsCard
                title="Unique Words"
                value={evaluationResults.uniqueWords.toString()}
                icon={FiAward}
              />
              <StatsCard
                title="Readability Score"
                value={`${evaluationResults.readabilityScore.toFixed(0)}/100`}
                icon={FiTrendingUp}
              />
              <StatsCard
                title="Difficulty Level"
                value={evaluationResults.difficultyLevel}
                icon={FiTarget}
              />
            </SimpleGrid>

            {/* Main Results */}
            <Card>
              <Tabs>
                <TabList>
                  <Tab>Grade Distribution</Tab>
                  <Tab>Difficulty Analysis</Tab>
                  {/* <Tab>Recommendations</Tab> */}
                  <Tab>Detailed Breakdown</Tab>
                </TabList>

                <TabPanels>
                  {/* Grade Distribution Tab */}
                  <TabPanel>
                    <VStack spacing={6} align="stretch">
                      <HStack justify="space-between">
                        <Text fontSize="lg" fontWeight="semibold">
                          Vocabulary Grade Distribution
                        </Text>
                        <HStack spacing={2}>
                          <Button
                            leftIcon={<FiBarChart />}
                            variant="secondary"
                            size="sm"
                            onClick={onDetailOpen}
                          >
                            View Charts
                          </Button>
                          <Button
                            leftIcon={<FiDownload />}
                            variant="secondary"
                            size="sm"
                            onClick={handleExportResults}
                          >
                            Export
                          </Button>
                        </HStack>
                      </HStack>

                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Grade Level</Th>
                            <Th>Word Count</Th>
                            <Th>Percentage</Th>
                            <Th>Cumulative %</Th>
                            <Th>Progress</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {evaluationResults.vocabularyAnalysis.map(
                            (item, index) => {
                              const gradeKey =
                                item.grade === 'Learned' ||
                                item.grade === 'New' ||
                                item.grade === 'Other'
                                  ? item.grade
                                  : item.grade.split(' ')[1];
                              return (
                                <Tr key={index}>
                                  <Td>
                                    <Badge variant="subtle">{item.grade}</Badge>
                                  </Td>
                                  <Td fontWeight="medium">
                                    {evaluationResults.gradeDistribution[
                                      gradeKey
                                    ] || 0}
                                  </Td>
                                  <Td>{item.percentage.toFixed(1)}%</Td>
                                  <Td>
                                    {evaluationResults.cumulativeDistribution[
                                      gradeKey
                                    ] !== undefined
                                      ? `${evaluationResults.cumulativeDistribution[
                                          gradeKey
                                        ].toFixed(1)}%`
                                      : '--'}
                                  </Td>
                                  <Td>
                                    <Progress
                                      value={item.percentage}
                                      size="sm"
                                      width="100px"
                                    />
                                  </Td>
                                </Tr>
                              );
                            },
                          )}
                        </Tbody>
                      </Table>
                    </VStack>
                  </TabPanel>

                  {/* Difficulty Analysis Tab */}
                  <TabPanel>
                    <VStack spacing={6} align="stretch">
                      <Text fontSize="lg" fontWeight="semibold">
                        Reading Difficulty Assessment
                      </Text>

                      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                        <Card variant="outline">
                          <VStack spacing={4}>
                            <HStack>
                              <FiTarget
                                size={24}
                                color={getDifficultyColor(
                                  evaluationResults.difficultyLevel,
                                )}
                              />
                              <Text fontSize="xl" fontWeight="bold">
                                Difficulty Level:{' '}
                                {evaluationResults.difficultyLevel}
                              </Text>
                            </HStack>

                            <Progress
                              value={evaluationResults.readabilityScore}
                              size="lg"
                              colorScheme={getDifficultyColor(
                                evaluationResults.difficultyLevel,
                              )}
                              width="100%"
                            />

                            <Text
                              fontSize="sm"
                              color="gray.600"
                              textAlign="center"
                            >
                              Readability Score:{' '}
                              {evaluationResults.readabilityScore.toFixed(0)}
                              /100
                              <br />
                              (Higher scores indicate easier reading)
                            </Text>
                          </VStack>
                        </Card>

                        <Card variant="outline">
                          <VStack align="stretch" spacing={4}>
                            <Text fontWeight="semibold">Difficulty Scale</Text>
                            <VStack spacing={3} align="stretch">
                              <HStack justify="space-between">
                                <HStack>
                                  <Box
                                    w={3}
                                    h={3}
                                    bg="green.500"
                                    borderRadius="full"
                                  />
                                  <Text fontSize="sm">
                                    Easy (0-15% high-grade words)
                                  </Text>
                                </HStack>
                                <Badge colorScheme="green" size="sm">
                                  90-100
                                </Badge>
                              </HStack>
                              <HStack justify="space-between">
                                <HStack>
                                  <Box
                                    w={3}
                                    h={3}
                                    bg="blue.500"
                                    borderRadius="full"
                                  />
                                  <Text fontSize="sm">Moderate (15-25%)</Text>
                                </HStack>
                                <Badge colorScheme="blue" size="sm">
                                  70-90
                                </Badge>
                              </HStack>
                              <HStack justify="space-between">
                                <HStack>
                                  <Box
                                    w={3}
                                    h={3}
                                    bg="orange.500"
                                    borderRadius="full"
                                  />
                                  <Text fontSize="sm">Challenge (25-40%)</Text>
                                </HStack>
                                <Badge colorScheme="orange" size="sm">
                                  50-70
                                </Badge>
                              </HStack>
                              <HStack justify="space-between">
                                <HStack>
                                  <Box
                                    w={3}
                                    h={3}
                                    bg="red.500"
                                    borderRadius="full"
                                  />
                                  <Text fontSize="sm">Difficult (40%+)</Text>
                                </HStack>
                                <Badge colorScheme="red" size="sm">
                                  0-50
                                </Badge>
                              </HStack>
                            </VStack>
                          </VStack>
                        </Card>
                      </SimpleGrid>
                    </VStack>
                  </TabPanel>

                  {/* Recommendations Tab */}
                  {/* <TabPanel>
                    <VStack spacing={6} align="stretch">
                      <Text fontSize="lg" fontWeight="semibold">
                        Teaching Recommendations
                      </Text>

                      <Alert
                        status={
                          evaluationResults.difficultyLevel === 'Difficult'
                            ? 'warning'
                            : 'success'
                        }
                        variant="left-accent"
                      >
                        <AlertIcon />
                        <VStack align="start" spacing={2}>
                          <Text fontWeight="semibold">
                            This article is classified as:{' '}
                            {evaluationResults.difficultyLevel}
                          </Text>
                          <Text fontSize="sm">
                            {evaluationResults.difficultyLevel === 'Easy' &&
                              'Perfect for beginner students. Contains mostly basic vocabulary.'}
                            {evaluationResults.difficultyLevel === 'Moderate' &&
                              'Suitable for intermediate students with some challenging words.'}
                            {evaluationResults.difficultyLevel ===
                              'Challenge' &&
                              'Good for advanced students. Contains significant complex vocabulary.'}
                            {evaluationResults.difficultyLevel ===
                              'Difficult' &&
                              'Very challenging. Consider pre-teaching vocabulary or simplifying.'}
                          </Text>
                        </VStack>
                      </Alert>

                      <Card variant="outline">
                        <VStack align="stretch" spacing={4}>
                          <Text fontWeight="semibold">
                            Specific Recommendations:
                          </Text>
                          <List spacing={3}>
                            {evaluationResults.recommendations.map(
                              (rec, index) => (
                                <ListItem key={index}>
                                  <ListIcon
                                    as={FiCheckCircle}
                                    color="green.500"
                                  />
                                  {rec}
                                </ListItem>
                              ),
                            )}
                            <ListItem>
                              <ListIcon as={FiInfo} color="blue.500" />
                              Consider the student's current level when
                              assigning this material
                            </ListItem>
                            <ListItem>
                              <ListIcon as={FiTarget} color="purple.500" />
                              Pre-teach high-frequency difficult words before
                              reading
                            </ListItem>
                          </List>
                        </VStack>
                      </Card>
                    </VStack>
                  </TabPanel> */}

                  {/* Detailed Breakdown Tab */}
                  <TabPanel>
                    <VStack spacing={6} align="stretch">
                      <HStack justify="space-between">
                        <Text fontSize="lg" fontWeight="semibold">
                          Statistical Analysis
                        </Text>
                        <Tooltip label="Refresh analysis">
                          <IconButton
                            aria-label="Refresh"
                            icon={<FiRefreshCw />}
                            variant="ghost"
                            onClick={() => handleEvaluateClick()}
                          />
                        </Tooltip>
                      </HStack>

                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        <Card variant="outline">
                          <VStack align="stretch" spacing={4}>
                            <Text fontWeight="semibold">Text Statistics</Text>
                            <Table size="sm">
                              <Tbody>
                                <Tr>
                                  <Td>Total Characters:</Td>
                                  <Td fontWeight="medium">{article.length}</Td>
                                </Tr>
                                <Tr>
                                  <Td>Total Words:</Td>
                                  <Td fontWeight="medium">
                                    {evaluationResults.wordCount}
                                  </Td>
                                </Tr>
                                <Tr>
                                  <Td>Unique Words:</Td>
                                  <Td fontWeight="medium">
                                    {evaluationResults.uniqueWords}
                                  </Td>
                                </Tr>
                                <Tr>
                                  <Td>Vocabulary Diversity:</Td>
                                  <Td fontWeight="medium">
                                    {(
                                      (evaluationResults.uniqueWords /
                                        evaluationResults.wordCount) *
                                      100
                                    ).toFixed(1)}
                                    %
                                  </Td>
                                </Tr>
                              </Tbody>
                            </Table>
                          </VStack>
                        </Card>

                        <Card variant="outline">
                          <VStack align="stretch" spacing={4}>
                            <Text fontWeight="semibold">
                              Grade Level Breakdown
                            </Text>
                            {evaluationResults.vocabularyAnalysis.map(
                              (item, index) => {
                                const gradeKey =
                                  item.grade === 'Learned' ||
                                  item.grade === 'New' ||
                                  item.grade === 'Other'
                                    ? item.grade
                                    : item.grade.split(' ')[1];
                                const count =
                                  evaluationResults.gradeDistribution[
                                    gradeKey
                                  ] || 0;
                                return (
                                  <HStack key={index} justify="space-between">
                                    <Text fontSize="sm">{item.grade}:</Text>
                                    <HStack>
                                      <Text fontSize="sm" fontWeight="medium">
                                        {count} words
                                      </Text>
                                      <Text fontSize="sm" color="gray.500">
                                        ({item.percentage.toFixed(1)}%)
                                      </Text>
                                    </HStack>
                                  </HStack>
                                );
                              },
                            )}
                          </VStack>
                        </Card>
                      </SimpleGrid>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Card>
          </>
        )}

        <Modal isOpen={isLibraryOpen} onClose={onLibraryClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Choose Existing Article</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={4} align="stretch">
                {libraryError && (
                  <Alert status="error">
                    <AlertIcon />
                    <Text>{libraryError}</Text>
                  </Alert>
                )}
                {isLibraryLoading ? (
                  <Text color="gray.600">Loading articles...</Text>
                ) : libraryArticles.length === 0 ? (
                  <Text color="gray.600">No articles found.</Text>
                ) : (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Title</Th>
                        <Th>Grade</Th>
                        <Th>Chapter</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {libraryArticles.map((entry) => (
                        <Tr key={entry.id}>
                          <Td>{entry.title}</Td>
                          <Td>{entry.grade}</Td>
                          <Td>{entry.chapter}</Td>
                          <Td textAlign="right">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() =>
                                handleSelectExistingArticle(entry.id)
                              }
                            >
                              Select
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Word Count Modal */}
        <Modal
          isOpen={isWordCountOpen}
          onClose={onWordCountClose}
          size="xl"
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Article Word Counts</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={4} align="stretch">
                {wordCountError && (
                  <Alert status="error">
                    <AlertIcon />
                    <Text>{wordCountError}</Text>
                  </Alert>
                )}
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between" align="center">
                    <Text fontSize="sm" color="gray.600">
                      {isWordCountUpdating
                        ? 'Updating word counts...'
                        : `${wordCountArticles.length} articles`}
                    </Text>
                    <Box maxW="200px">
                      <FormField label="Order">
                        <SelectInput
                          value={wordCountOrder}
                          onChange={(value) =>
                            setWordCountOrder(value as WordCountOrder)
                          }
                          options={[
                            { label: 'Ascending', value: 'asc' },
                            { label: 'Descending', value: 'desc' },
                          ]}
                        />
                      </FormField>
                    </Box>
                  </HStack>
                  {(isWordCountUpdating || wordCountStatus) && (
                    <Text fontSize="sm" color="gray.500">
                      {wordCountStatus}
                    </Text>
                  )}
                </VStack>
                {isWordCountUpdating ? (
                  <Text color="gray.600">Updating articles...</Text>
                ) : wordCountArticles.length === 0 ? (
                  <Text color="gray.600">No articles found.</Text>
                ) : (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Title</Th>
                        <Th isNumeric>Word Count</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {wordCountPageItems.map((entry) => (
                        <Tr key={entry.id}>
                          <Td>{entry.title}</Td>
                          <Td isNumeric>
                            {entry.word_count !== null
                              ? entry.word_count
                              : '--'}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
                {wordCountArticles.length > 0 && (
                  <HStack justify="space-between" align="center">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        setWordCountPage((prev) => Math.max(1, prev - 1))
                      }
                      isDisabled={wordCountPage <= 1}
                    >
                      Previous
                    </Button>
                    <Text fontSize="sm" color="gray.600">
                      Page {wordCountPage} of {wordCountTotalPages}
                    </Text>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        setWordCountPage((prev) =>
                          Math.min(wordCountTotalPages, prev + 1),
                        )
                      }
                      isDisabled={wordCountPage >= wordCountTotalPages}
                    >
                      Next
                    </Button>
                  </HStack>
                )}
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Charts Modal */}
        <Modal
          isOpen={isDetailOpen}
          onClose={onDetailClose}
          size="6xl"
          isCentered
        >
          <ModalOverlay />
          <ModalContent maxW="90vw">
            <ModalHeader>Vocabulary Distribution Charts</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
                <Box>
                  <Text fontSize="lg" fontWeight="semibold" mb={4}>
                    Grade Distribution (Bar Chart)
                  </Text>
                  <BarChart
                    data={evaluationResults.gradeDistribution}
                    index="y"
                    width="100%"
                    height="400px"
                  />
                </Box>
                <Box>
                  <Text fontSize="lg" fontWeight="semibold" mb={4}>
                    Grade Distribution (Pie Chart)
                  </Text>
                  <PieChart data={evaluationResults.gradeDistribution} />
                </Box>
              </SimpleGrid>
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </Layout>
  );
};
